import React, {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { isKey } from '../../../../helpers/keyboard';
import {
  useClickAway,
  useMaybeControlledValue,
  useRefAndForward,
  useValidityState,
  type ValidityChangeCallback,
} from '../../../../hooks';
import { DownFilledIcon } from '../../../Icon/icons';
import { Overlay } from '../../../Overlay';
import { Control, type ControlProps } from '../../Control';
import { type SelectContext, SelectProvider } from '../context';
import { getOptionElements } from '../getOptionElements';
import { Styled } from '../styles';
import { useSearch } from '../useSearch';

type ControlPropsToForward = Omit<ComponentPropsWithoutRef<'div'>, 'ref' | keyof ControlProps> & ControlProps;
type OverlayProps = ComponentPropsWithoutRef<typeof Overlay>;
type OverlayPropsToForward = {
  children?: OverlayProps['children'];
  overlayMaxHeight?: OverlayProps['maxHeight'];
};
type InputPropsToForward = Pick<ComponentPropsWithoutRef<'input'>, 'required' | 'name' | 'form'>;

type SelectProps<T> = {
  placeholder?: string;
  controlRef?: ForwardedRef<HTMLElement | null>;
  search?: string | null;
  onSearchChange?: (search: string | null) => void;
  isOpened?: boolean;
  onOpenedChange?: (isOpened: boolean) => void;
  optionsWrapperId?: string;
  onValidityChange?: ValidityChangeCallback;
  values: T[] | null;
  onChange: (value: T | null) => void;
  onCloseOverlay?: () => void;
  formatLabel?: (value: T) => string;
  onFocus?: (e: FocusEvent) => void;
  disableKeyboardArrowSelection?: boolean;
  disablePortalForOptions?: boolean;
  shouldDisplaySelectedValuesInsideInput?: boolean;
  shouldAllowLargerWidthToFitContent?: boolean;
};

type Props<T> = Omit<OverlayPropsToForward & ControlPropsToForward & InputPropsToForward, keyof SelectProps<T>> &
  SelectProps<T>;

const defaultSuffix = <DownFilledIcon size="m" />;

export const SelectControlMulti = forwardRef<HTMLDivElement, Props<any>>(
  (
    {
      controlRef = null,
      onChange,
      onCloseOverlay,
      values,
      search,
      isOpened: maybeIsOpened,
      onOpenedChange: maybeOnOpenedChange,
      optionsWrapperId,
      onSearchChange,
      formatLabel = String,
      overlayMaxHeight,
      children,
      placeholder,
      required = false,
      suffix = defaultSuffix,
      onValidityChange,
      onFocus,
      disableKeyboardArrowSelection = false,
      disablePortalForOptions,
      shouldDisplaySelectedValuesInsideInput = false,
      shouldAllowLargerWidthToFitContent = false,
      ...controlProps
    },
    ref,
  ) => {
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const [isOpened, onOpenedChange] = useMaybeControlledValue({
      valueFromProps: maybeIsOpened,
      onChangeFromProps: maybeOnOpenedChange,
      defaultValue: false,
    });
    const isSearchable = !!onSearchChange && search !== undefined;
    const overlayRef = useRefAndForward<HTMLDivElement | null>(null, ref);
    const controlLocalRef = useRefAndForward<HTMLElement | null>(null, controlRef);

    const options = useMemo(() => getOptionElements(children), [children]);

    const handleChange = useCallback(
      (newValue: any | null, index?: number) => {
        if (index) {
          setFocusedIndex(prev => (prev === index ? -1 : index));
        }

        onChange(newValue);
      },
      [onChange],
    );

    const context: SelectContext<any> = useMemo(
      () => ({
        formatLabel: (optionValue: any) => (optionValue ? formatLabel(optionValue) : ''),
        onChange: handleChange,
        values,
        value: null,
        variant: 'select',
        indexOfFocusedItem: focusedIndex,
      }),
      [values, focusedIndex, formatLabel, handleChange],
    );

    const labelFromValue =
      shouldDisplaySelectedValuesInsideInput && values
        ? values.map(value => context.formatLabel(value)).join(', ')
        : '';
    const input = useSearch({ labelFromValue, onSearchChange, search, placeholder, isOpened });

    const handleCloseOverlay = useCallback(() => {
      onOpenedChange(false);
      onCloseOverlay?.();
    }, [onCloseOverlay, onOpenedChange]);

    useClickAway({
      containerRefs: [controlLocalRef, overlayRef],
      listener: useCallback(() => {
        if (!isOpened) return;

        handleCloseOverlay();
      }, [handleCloseOverlay, isOpened]),
    });

    useValidityState({
      onValidityChange,
      getState: useCallback(
        () => ({ valueMissing: required && (values === null || !values.length) }),
        [required, values],
      ),
    });

    const commonControlProps: Partial<ComponentPropsWithoutRef<typeof Control>> = {
      ref: controlLocalRef,
      role: 'listbox',
      suffix,
      onFocus: (e: FocusEvent) => {
        if (controlProps.disabled) return;
        onOpenedChange(true);
        if (onFocus) {
          onFocus(e);
        }
      },
      style: {
        pointerEvents: !controlProps.disabled ? 'auto' : 'none',
        cursor: !controlProps.disabled ? 'pointer' : 'not-allowed',
        minHeight: '2.625rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      },
      'aria-multiselectable': true,
      'aria-required': required,
    };

    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (!isOpened || disableKeyboardArrowSelection || !options.length) return;

        if (isKey('enter')(event)) {
          event.preventDefault();

          if (focusedIndex !== -1) {
            const nextValue = options[focusedIndex]?.props?.value;
            onChange(nextValue);
          }
        }

        if (isKey('escape')(event)) {
          handleCloseOverlay();
        }

        if (isKey('up')(event)) {
          event.preventDefault();
          setFocusedIndex(prev => (prev - 1 + options.length) % options.length);
        }

        if (isKey('bottom')(event)) {
          event.preventDefault();
          setFocusedIndex(prev => (prev + 1) % options.length);
        }
      },
      [disableKeyboardArrowSelection, focusedIndex, options, handleCloseOverlay, isOpened, onChange],
    );

    return (
      <SelectProvider value={context}>
        {isSearchable ? (
          <Control
            as="input"
            placeholder={input.placeholder}
            value={input.value}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onSearchChange?.(event.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            {...controlProps}
            {...commonControlProps}
          />
        ) : (
          <Control {...controlProps} {...commonControlProps} tabIndex={0}>
            {shouldDisplaySelectedValuesInsideInput &&
              values &&
              values.map(value => context.formatLabel(value)).join(', ')}
            {!values && placeholder && <Styled.Placeholder>{placeholder}</Styled.Placeholder>}
          </Control>
        )}

        <Overlay
          variant="fit-target"
          ref={overlayRef}
          targetRef={controlLocalRef}
          isOpened={isOpened && React.Children.count(children) > 0}
          maxHeight={overlayMaxHeight}
          disablePortalForOptions={disablePortalForOptions}
          shouldFitContentWidth={shouldAllowLargerWidthToFitContent}
          id={optionsWrapperId}
        >
          {children}
        </Overlay>
      </SelectProvider>
    );
  },
);
SelectControlMulti.displayName = 'Select.ControlMulti';
